import {
  Filter,
  type FilterProps,
  type FilterRenderOptionsProps,
} from '@terminal/components/filter/Filter';
import type { IncludeExcludeLabels } from '@terminal/components/filter/include-exclude/include-exclude.types';
import { MultiselectIncludeExcludeOptions } from '@terminal/components/filter/include-exclude/multiselect/MultiselectIncludeExcludeOptions';
import { getDefaultFilterOption } from '@terminal/components/filter/include-exclude/utils/include-exclude.utils';
import type { FilterGroupKeys } from '@terminal/hooks/use-filter-actions/useFilterActions';

export type MultiselectIncludeExcludeFilterProps<
  T extends FilterGroupKeys = FilterGroupKeys,
> = Omit<FilterProps<T>, 'labels' | 'renderOptions' | 'variant'> & {
  labels: FilterProps<T>['labels'] & {
    includeExcludeLabels: IncludeExcludeLabels;
  };
};

export const MultiselectIncludeExcludeFilter = <T extends FilterGroupKeys>(
  props: MultiselectIncludeExcludeFilterProps<T>
) => {
  const {
    addFilterOption,
    disabledOptions,
    filter,
    filterOptionGroups = [],
    id = filter.key,
    labels,
    removeFilterOption,
  } = props;

  const renderOptions = ({ options, showEmpty }: FilterRenderOptionsProps) => (
    <MultiselectIncludeExcludeOptions
      addFilterOption={addFilterOption}
      defaultOption={getDefaultFilterOption(
        filter.config.options,
        filter.config.default
      )}
      disabledOptions={disabledOptions}
      disabledTooltipLabel={labels.disabledTooltipLabel}
      filterId={id}
      filterKey={filter.key}
      filterOptionGroups={filterOptionGroups}
      labels={labels.includeExcludeLabels}
      minimumSelection={filter.config.min_select}
      noResultsLabel={labels.noResultsLabel}
      options={options}
      removeFilterOption={removeFilterOption}
      renderCustomFilterOptionLabel={labels.renderCustomFilterOptionLabel}
      showEmpty={showEmpty}
    />
  );

  return (
    <Filter
      {...props}
      filterOptionGroups={filterOptionGroups}
      id={id}
      renderOptions={renderOptions}
      variant="include-exclude-multiselect"
    />
  );
};
